import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export type OfficerIdentity = {
  id: string;
  name?: string;
};

export type SessionSnapshot = {
  officer?: OfficerIdentity;
};

const STORAGE_KEYS = ["phmis.session", "phmis_session", "phmis.currentSession"];
const OFFICER_ID_KEYS = ["phmis.officerId", "phmis.officer.id", "officerId", "user.officerId"];
const OFFICER_NAME_KEYS = ["phmis.officerName", "phmis.officer.name", "officerName", "user.officerName"];
const SESSION_EVENT = "phmis:session";

type MaybeStorage = Pick<Storage, "getItem"> | undefined;

type AnyObject = Record<string, unknown>;

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function readOfficerFromObject(value: unknown): OfficerIdentity | null {
  if (!value || typeof value !== "object") return null;
  const obj = value as AnyObject;
  const officerLike = (obj.officer && typeof obj.officer === "object" ? obj.officer as AnyObject : obj);
  const id = ["id", "officerId"].map((key) => officerLike?.[key]).find((v): v is string => typeof v === "string" && v.length > 0)
    ?? OFFICER_ID_KEYS.map((key) => obj[key]).find((v): v is string => typeof v === "string" && v.length > 0);
  const name = ["name", "officerName"].map((key) => officerLike?.[key]).find((v): v is string => typeof v === "string" && v.length > 0)
    ?? OFFICER_NAME_KEYS.map((key) => obj[key]).find((v): v is string => typeof v === "string" && v.length > 0);
  if (!id) return null;
  return { id, name };
}

function readOfficerFromStorage(storage: MaybeStorage): OfficerIdentity | null {
  if (!storage) return null;
  for (const key of STORAGE_KEYS) {
    const parsed = safeParse<SessionSnapshot | AnyObject>(storage.getItem(key));
    const officer = readOfficerFromObject(parsed ?? undefined);
    if (officer) return officer;
  }
  const direct: AnyObject = {};
  for (const key of OFFICER_ID_KEYS) {
    const value = storage.getItem(key);
    if (value) direct[key] = value;
  }
  for (const key of OFFICER_NAME_KEYS) {
    const value = storage.getItem(key);
    if (value) direct[key] = value;
  }
  return readOfficerFromObject(direct);
}

function readGlobalOfficer(): OfficerIdentity | null {
  if (typeof window === "undefined") return null;
  const anyWindow = window as unknown as AnyObject;
  const globalCandidates = [
    anyWindow.__PHMIS_SESSION__,
    anyWindow.PHMIS_SESSION,
    anyWindow.session,
    anyWindow.currentUser,
  ];
  for (const candidate of globalCandidates) {
    const officer = readOfficerFromObject(candidate);
    if (officer) return officer;
  }
  return null;
}

export function getCurrentOfficer(): OfficerIdentity | null {
  if (typeof window === "undefined") return null;
  const storages: MaybeStorage[] = [window.sessionStorage, window.localStorage];
  for (const storage of storages) {
    const officer = readOfficerFromStorage(storage);
    if (officer) return officer;
  }
  return readGlobalOfficer();
}

const SessionContext = createContext<OfficerIdentity | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const initial = useMemo(() => getCurrentOfficer(), []);
  const [officer, setOfficer] = useState<OfficerIdentity | null>(initial);

  useEffect(() => {
    const update = () => setOfficer(getCurrentOfficer());
    window.addEventListener("storage", update);
    window.addEventListener(SESSION_EVENT, update as EventListener);
    return () => {
      window.removeEventListener("storage", update);
      window.removeEventListener(SESSION_EVENT, update as EventListener);
    };
  }, []);

  return <SessionContext.Provider value={officer}>{children}</SessionContext.Provider>;
}

export function useCurrentOfficer(): OfficerIdentity | null {
  return useContext(SessionContext);
}

export function notifySessionChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SESSION_EVENT));
}
